// Curated MSSQL T-SQL keyword list emitted by `mssqlResolver`. Tier 7
// (per RESEARCH.md §5.2 ranking tuple). The ranker handles casing through
// `applyKeywordCasing` when an `intellisenseSettings.keywordCasing` hint is
// threaded through. The labels stay UPPER here so the `match-user` mode's
// dominant-case fallback has a stable reference and so the un-cased default
// matches SSMS's default rendering.
//
// Selection criteria: keywords that surface in normal user authoring flows
// (DML, DDL, query-shaping, transactional control). Reserved words that
// only appear inside system-generated DDL (e.g. CONSTRAINT names) are
// excluded — they belong in deeper position-conditional emissions added in
// later slices.

const TSQL_KEYWORDS: readonly string[] = [
  'ADD', 'ALL', 'ALTER', 'AND', 'ANY', 'AS', 'ASC', 'BACKUP', 'BEGIN',
  'BETWEEN', 'BREAK', 'BY', 'CASE', 'CAST', 'CHECK', 'CHECKPOINT',
  'CLOSE', 'CLUSTERED', 'COLUMN', 'COMMIT', 'CONSTRAINT', 'CONTINUE',
  'CONVERT', 'CREATE', 'CROSS', 'CURRENT', 'CURRENT_DATE',
  'CURRENT_TIME', 'CURRENT_TIMESTAMP', 'CURRENT_USER', 'CURSOR',
  'DATABASE', 'DBCC', 'DEALLOCATE', 'DECLARE', 'DEFAULT', 'DELETE',
  'DENY', 'DESC', 'DISTINCT', 'DROP', 'ELSE', 'END', 'EXCEPT', 'EXEC',
  'EXECUTE', 'EXISTS', 'EXIT', 'FETCH', 'FILE', 'FILLFACTOR', 'FOR',
  'FOREIGN', 'FROM', 'FULL', 'FUNCTION', 'GOTO', 'GRANT', 'GROUP',
  'HAVING', 'IDENTITY', 'IF', 'IN', 'INDEX', 'INNER', 'INSERT',
  'INTERSECT', 'INTO', 'IS', 'JOIN', 'KEY', 'KILL', 'LEFT', 'LIKE',
  'MAX', 'MERGE', 'NATIONAL', 'NEXT', 'NOCHECK', 'NOT', 'NULL', 'NULLIF',
  'OF', 'OFF', 'OFFSET', 'ON', 'ONLY', 'OPEN', 'OPTION', 'OR', 'ORDER',
  'OUTER', 'OUTPUT', 'OVER', 'PARSE', 'PERCENT', 'PIVOT', 'PLAN', 'PRIMARY',
  'PRINT', 'PROC', 'PROCEDURE', 'PUBLIC', 'RAISERROR', 'READ', 'READTEXT',
  'RECONFIGURE', 'REFERENCES', 'RETURN', 'REVERT', 'REVOKE', 'RIGHT',
  'ROLLBACK', 'ROWCOUNT', 'ROWS', 'RULE', 'SAVE', 'SCHEMA', 'SELECT',
  'SESSION_USER', 'SET', 'SETUSER', 'SHUTDOWN', 'SOME', 'SUM',
  'SYSTEM_USER', 'TABLE', 'TEXTSIZE', 'THEN', 'TO', 'TOP', 'TRAN',
  'TRANSACTION', 'TRIGGER', 'TRUNCATE', 'TRY_CAST', 'TRY_CONVERT',
  'TRY_PARSE', 'UNION', 'UNIQUE', 'UPDATE', 'UPDATETEXT', 'USE', 'USER',
  'VALUES', 'VARYING', 'VIEW', 'WAITFOR', 'WHEN', 'WHERE', 'WHILE',
  'WITH', 'WITHIN', 'WRITETEXT',
];

export const MSSQL_KEYWORDS: ReadonlySet<string> = new Set(TSQL_KEYWORDS);

/**
 * Subset emitted at clause-start positions to keep the suggest list
 * focused. Position-aware emission is owned by per-position handlers in
 * `mssqlResolver.ts`. Right now this just returns the whole list — the
 * orchestrator's quality-filtering keeps the popup useful.
 */
export function getMssqlKeywordsForPosition(): readonly string[] {
  return TSQL_KEYWORDS;
}

/**
 * Multi-word T-SQL keywords typed one word at a time — the vocabulary
 * `compoundKeywordRemainder.ts` completes from a written head plus a partial
 * (`NEXT V▮` → `VALUE FOR`).
 *
 * Only compounds `context/legalNextKeywords.ts` does NOT already carry for
 * mssql belong here; that module is the per-slot source of truth and the
 * remainder scan reads both. Sources:
 *   • `NEXT VALUE FOR` — the sequence value expression (Microsoft docs,
 *     "NEXT VALUE FOR (Transact-SQL)"). `mssqlResolver.ts` emits it whole at a
 *     select-list caret (coverage script.42 §5) but had nothing to offer once
 *     `NEXT ` was written.
 *   • `UNBOUNDED FOLLOWING` — the window frame END bound (Microsoft docs,
 *     "OVER Clause"). `legalNextKeywords` models only the frame-START
 *     vocabulary (`after-window-frame-unit`); the direction-scoped lists live
 *     in `mssqlResolver.ts` (`MSSQL_WINDOW_FRAME_START_KEYWORDS` /
 *     `MSSQL_WINDOW_FRAME_END_KEYWORDS`) — keep this entry in sync with them.
 */
export const MSSQL_COMPOUND_KEYWORDS: readonly string[] = [
  'NEXT VALUE FOR',
  'UNBOUNDED FOLLOWING',
];
